export interface Channel {
  id: string;
  slug: string;
  name: string;
  image_url?: string;
}

export interface Post {
  id: string;
  title: string;
  body?: string;
  user_profile_id: string
  media_urls?: string[];
}

export interface Comment {
  id: string;
  post_id: string;
  user_profile_id: string;
  text: string;
  created_at: string;
}

export interface User {
  id: string;
  display_name: string;
  avatar_url: string;
}

export interface UserProfile {
  id: string;
  display_name: string;
  slug: string;
  bio: string;
  avatar_url: string;
}

export interface SearchResults {
  channels: Channel[];
  posts: Post[];
  users: User[];
}

export class GoonAPIClient {
  constructor(private baseUrl: string) { }

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(`${this.baseUrl}${url}`, { ...options, credentials: 'include' });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return await res.json();
  }

  signin(token: string): Promise<{ user: User }> {
    return this.request('/auth/signin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
  }

  async getAuthStatus(): Promise<{ user_id: string }> {
    const res = await fetch(`${this.baseUrl}/auth/status`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Unauthorized');
    return res.json();
  }

  createChannel(data: { name: string; imageUrl: string }): Promise<Channel> {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('image_url', data.imageUrl);

    return this.request('/channels', {
      method: 'POST',
      body: formData
    });
  }

  async uploadMediaAsync(
    file: File,
    contentKey: 'channel-image' | 'post-content' | 'persona',
    mimeType: string
  ): Promise<{ job_id: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contentKey', contentKey);
    formData.append('mimeType', mimeType);
    return this.request('/media/upload-async', {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Checks the status of an async transcode job.
   */
  async getTranscodeStatus(jobId: string): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled';
    url?: string;
    error?: string;
  }> {
    return this.request(`/media/transcode-status/${jobId}`);
  }

  /**
   * Cancels an async transcode job.
   */
  async cancelTranscode(jobId: string): Promise<{ status: string }> {
    return this.request(`/media/cancel-transcode/${jobId}`, {
      method: 'POST',
    });
  }

  /**
 * Uploads a media file and waits for transcoding to complete.
 * Returns the final media URL.
 */
  async uploadMediaAndWait(
    file: File,
    contentKey: 'channel-image' | 'post-content' | 'persona',
    mimeType: string,
    pollIntervalMs = 2000
  ): Promise<string> {
    const { job_id } = await this.uploadMediaAsync(file, contentKey, mimeType);

    return new Promise<string>((resolve, reject) => {
      const poll = async () => {
        try {
          const res = await this.getTranscodeStatus(job_id);
          if (res.status === 'completed' && res.url) {
            return resolve(res.url);
          }
          if (res.status === 'failed') {
            return reject(new Error(res.error || 'Transcoding failed'));
          }
          if (res.status === 'canceled') {
            return reject(new Error('Transcoding was canceled'));
          }
          setTimeout(poll, pollIntervalMs);
        } catch (err) {
          reject(err);
        }
      };
      poll();
    });
  }

  createPost(data: { title: string; body?: string; channelId: string; mediaUrls?: string[] }): Promise<Post> {
    return this.request('/posts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }

  getPostById(id: string): Promise<Post> {
    return this.request(`/posts/${id}`);
  }

  getChannelBySlug(slug: string): Promise<Channel> {
    return this.request(`/channels/slug/${slug}`);
  }

  getPaginatedChannelPosts(channelSlug: string, page: number, sort?: 'hot' | 'new' | 'top', range?: 'today' | 'week' | 'month' | 'year'): Promise<{ posts: Post[]; users: UserProfile[] }> {
    const params = new URLSearchParams({ page: page.toString() });
    if (sort) params.set('sort', sort);
    if (range) params.set('range', range);
    return this.request(`/channels/${channelSlug}/posts?${params}`);
  }

  getUserProfileBySlug(profileSlug: string): Promise<UserProfile> {
    return this.request(`/user-profile/${profileSlug}`);
  }

  createPostComment(postId: string, body: string): Promise<Comment> {
    return this.request(`/posts/${postId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body })
    });
  }

  getPostComments(postId: string): Promise<Comment[]> {
    return this.request(`/posts/${postId}/comments`);
  }

  setPostReaction(postId: string, emoji: string): Promise<void> {
    return this.request(`/posts/${postId}/reactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emoji })
    });
  }

  getPostReactions(postId: string): Promise<{
    counts: Record<string, number>;
    userReaction: string;
  }> {
    return this.request(`/posts/${postId}/reactions`);
  }

  joinChannel(channelId: string): Promise<void> {
    return this.request(`/channels/${channelId}/join`, { method: 'POST' });
  }

  listChannels(): Promise<Channel[]> {
    return this.request(`/channels`);
  }

  connectChatSocket(channelSlug: string): WebSocket {
    const ws = new WebSocket(`${this.baseUrl.replace('http', 'ws')}/channels/${channelSlug}/chat`);
    return ws;
  }

  sendChatMessage(ws: WebSocket, message: { text: string; postId?: string }) {
    ws.send(JSON.stringify(message));
  }

  getCurrentUserProfile(): Promise<UserProfile> {
    return this.request(`/me`);
  }

  getHotChannels(): Promise<{ channels: Channel[] }> {
    return this.request(`/channels/hot`);
  }

  getMyProfiles(): Promise<{ profiles: UserProfile[] }> {
    return this.request('/me/profiles');
  }

  switchProfile(profileId: string): Promise<{ status: string; profile_id: string }> {
    return this.request('/auth/switch-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_id: profileId })
    });
  }

  getUserProfilePosts(profileId: string, page = 1): Promise<Post[]> {
    return this.request(`/user/${profileId}/posts?page=${page}`);
  }

  createUserProfile(data: {
    display_name: string;
    avatar_url?: string;
    bio?: string;
  }): Promise<{ id: string }> {
    return this.request('/me/profiles/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  updateUserProfile(data: {
    id: string;
    display_name: string;
    avatar_url?: string;
    bio?: string;
  }): Promise<{ status: string }> {
    return this.request('/me/profiles/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getUserProfilePinnedPosts(profileId: string): Promise<Post[]> {
    const res = await this.request(`/user/${profileId}/pinned`);
    return res.posts;
  }

  followUser(profileId: string): Promise<{ status: string }> {
    return this.request(`/user/${profileId}/follow`, {
      method: 'POST',
    });
  }

  unfollowUser(profileId: string): Promise<{ status: string }> {
    return this.request(`/user/${profileId}/unfollow`, {
      method: 'POST',
    });
  }

  getFollowingProfiles(): Promise<{ following: UserProfile[] }> {
    return this.request('/me/following');
  }

  search(query: string): Promise<SearchResults> {
    return this.request(`/search?q=${encodeURIComponent(query)}`);
  }

  async fetchPageTitle(url: string): Promise<string> {
    const data = await this.request(`/fetch-page-title?url=${encodeURIComponent(url)}`);
    return data.title;
  }

  async fetchLinkPreview(url: string): Promise<{
    type: 'oembed' | 'opengraph',
    previewUrl: string,
    title?: string,
    description?: string,
    thumbnailUrl?: string
  }> {
    const res = await this.request(`/link-preview?url=${encodeURIComponent(url)}`);
    return res;
  }

  async addComment(post_id: string, text: string) {
    return this.request('/comments', { body: JSON.stringify({ post_id, text }), method: 'POST' });
  }

  async getComments(post_id: string): Promise<{
    users: UserProfile[];
    comments: Comment[];
  }> {
    return this.request(`/comments/${post_id}`);
  }

  async deleteComment(comment_id: string) {
    return this.request(`/comments/${comment_id}`, { method: 'DELETE' });
  }

  async unsetPostReaction(postId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/posts/${postId}/reaction`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error('Failed to unset reaction');
    }
  }

  async deletePost(postId: string): Promise<void> {
    await this.request(`/posts/${postId}`, {
      method: 'DELETE',
    });
  }

}

